import { normalize } from './text'
import { itemPath, mapPath, monsterPath } from './worldFormat'
import { englishFor } from '../data/guide/itemTranslations'
import { recipes, recipePath } from '../data/guide/recipes'
import { thirdEvolution } from '../data/guide/evolutions'
import type { WorldLookup } from './progressionLookup'

export type IntentOption = { label: string; description?: string; to: string }
export type SearchIntent = { id: string; title: string; options: IntentOption[] }

/** Lo que el jugador quiere hacer, detectado ANTES de quitar palabras de relleno. */
export type QueryAction = 'obtener' | 'receta' | 'drops' | 'acceso'

const WINGS = new Set(['ala', 'alas', 'wing', 'wings', 'capa', 'capas', 'cape', 'capes'])
const EVO = new Set(['evo', 'evolucion', 'evoluciones', 'evolucionar', 'evoluciono', 'generacion', 'nivel', 'gen'])
const ORDINALS: Record<string, number> = { '1': 1, primera: 1, primeras: 1, primer: 1, '2': 2, segunda: 2, segundas: 2, '3': 3, tercera: 3, terceras: 3 }

/** Expresiones de intención. Se evalúan sobre la frase completa, en este orden. */
const ACTIONS: { action: QueryAction; patterns: RegExp[] }[] = [
  { action: 'acceso', patterns: [/\bcomo (entro|entrar|accedo|acceder|llego|llegar|voy|ir)\b/, /\b(acceso|entrada|requisitos?) (a|al|de|del|para)\b/, /\bque nivel (necesito|hace falta|piden?)\b/, /\bhow (to )?(enter|get to)\b/] },
  { action: 'drops', patterns: [/\bque (dropea|dropean|suelta|sueltan|tira|da|deja)\b/, /\b(drops?|botin|loot) (de|del)\b/, /\bwhat (does )?.* drop\b/] },
  { action: 'receta', patterns: [/\bcomo (hago|hacer|creo|crear|fabrico|fabricar|armo|combino|mezclo)\b/, /\b(receta|crafteo|combinacion) (de|del|para)\b/, /\bhow (to )?(make|craft|create)\b/] },
  { action: 'obtener', patterns: [/\bdonde (consigo|conseguir|cae|caen|sale|salen|saco|dropea|encuentro|compro|obtengo)\b/, /\bcomo (consigo|conseguir|obtengo|obtener)\b/, /\bwhere (to |do i )?(get|find|farm|drop)\b/] },
]

const FILLER = new Set([
  'como', 'donde', 'que', 'cual', 'entro', 'entrar', 'accedo', 'acceder', 'llego', 'llegar', 'voy', 'ir', 'acceso', 'entrada', 'requisito', 'requisitos',
  'nivel', 'necesito', 'hace', 'falta', 'pide', 'piden', 'dropea', 'dropean', 'suelta', 'sueltan', 'tira', 'da', 'deja', 'drop', 'drops', 'botin', 'loot',
  'hago', 'hacer', 'creo', 'crear', 'fabrico', 'fabricar', 'armo', 'combino', 'mezclo', 'receta', 'crafteo', 'combinacion', 'consigo', 'conseguir', 'cae',
  'caen', 'sale', 'salen', 'saco', 'encuentro', 'compro', 'obtengo', 'obtener', 'a', 'al', 'de', 'del', 'el', 'la', 'los', 'las', 'un', 'una', 'para', 'en',
  'mi', 'mis', 'how', 'to', 'where', 'what', 'does', 'do', 'i', 'get', 'find', 'enter', 'make', 'craft', 'create', 'the', 'farm',
])

export function detectAction(query: string): QueryAction | null {
  const q = normalize(query)
  return ACTIONS.find((a) => a.patterns.some((p) => p.test(q)))?.action ?? null
}

/** Entidad que queda tras quitar las palabras de intención. */
function subjectOf(query: string): string {
  return normalize(query)
    .split(/[^a-z0-9'+-]+/)
    .filter((w) => w && !FILLER.has(w))
    .join(' ')
}

const wingsOption = (n: number): IntentOption => ({
  label: `Crear alas Evo ${n}`,
  description: `Alas de ${n === 1 ? 'primera' : n === 2 ? 'segunda' : 'tercera'} generación`,
  to: `/guia/alas/alas-evo-${n}`,
})

/** Acceso directo según la intención: acceso a un mapa, drops de un monstruo u obtención/receta de un ítem. */
function actionIntent(query: string, lookup?: WorldLookup): SearchIntent | null {
  const action = detectAction(query)
  if (!action || !lookup) return null
  const subject = subjectOf(query)
  if (subject.length < 3) return null
  const original = englishFor(subject) ?? subject

  if (action === 'acceso') {
    const m = lookup.map(original)
    if (!m) return null
    const options: IntentOption[] = [{ label: `Accesos y requisitos de ${m.name}`, description: 'Entrada y requisitos del mapa', to: `${mapPath(m.id)}#accesos` }]
    // Misión que usa el mapa: resultado relacionado, después del acceso.
    const evoMaps = [thirdEvolution.stage2.map, thirdEvolution.stage3.map].map(normalize)
    if (evoMaps.some((x) => normalize(m.name).split(' ').includes(x) || normalize(m.name) === x)) {
      options.push({ label: 'Relacionado: tercera evolución', description: 'Misión de Devin que se hace en este mapa', to: '/guia/evoluciones#tercera' })
    }
    return { id: `acceso-${m.id}`, title: 'Acceso al mapa', options }
  }
  if (action === 'drops') {
    const id = lookup.monster(original)
    if (id !== undefined) return { id: `drops-${id}`, title: 'Recompensas del monstruo', options: [{ label: `Ítems que puede soltar ${original}`, description: 'Combinaciones posibles, no garantizadas', to: `${monsterPath(id)}#drops` }] }
    return null
  }
  const item = lookup.identify({ gameName: original })
  if (action === 'receta') {
    const recipe = recipes.find((r) => r.results.some((x) => normalize(x.gameName) === normalize(original)) || r.aliases.some((a) => normalize(a) === subject))
    if (recipe) return { id: `receta-${recipe.id}`, title: 'Receta', options: [{ label: recipe.title, description: 'Ingredientes y pasos', to: recipePath(recipe.id) }] }
  }
  if (item) return { id: `obtener-${item.id}`, title: 'Dónde conseguirlo', options: [{ label: `Dónde conseguir ${item.name}`, description: 'Rutas de obtención, monstruos y ubicaciones', to: `${itemPath(item.id)}#donde-conseguirlo` }] }
  return null
}

/**
 * Reglas para resolver la intención de la búsqueda. No interpreta preguntas libres ni deduce datos:
 * solo ofrece accesos a fichas y guías existentes.
 */
export function detectIntent(query: string, lookup?: WorldLookup): SearchIntent | null {
  const words = normalize(query)
    .replace(/(evo|wings?|alas)(\d)/g, '$1 $2')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
  if (!words.length) return null
  const wings = words.some((w) => WINGS.has(w))
  const capes = words.some((w) => w.startsWith('cap'))
  const evo = words.some((w) => EVO.has(w))
  const num = words.map((w) => ORDINALS[w]).find((n) => n !== undefined)

  if (wings && capes && !num) {
    return { id: 'capas', title: 'Capas', options: [{ label: 'Crear capas', description: 'Dark Lord y Rage Fighter', to: '/guia/alas/capas' }] }
  }
  if (wings && num) return { id: `alas-${num}`, title: 'Receta', options: [wingsOption(num)] }
  if (wings) {
    return {
      id: 'alas',
      title: 'Guía de alas y capas',
      options: [
        { label: 'Ver la guía de alas', description: 'Todas las recetas en orden', to: '/guia/alas' },
        { label: 'Evo 1', to: '/guia/alas/alas-evo-1' },
        { label: 'Evo 2', to: '/guia/alas/alas-evo-2' },
        { label: 'Evo 3', to: '/guia/alas/alas-evo-3' },
        { label: 'Capas', to: '/guia/alas/capas' },
      ],
    }
  }
  if (evo && num === 2) {
    return { id: 'evo-2', title: '¿Qué quieres hacer?', options: [{ label: 'Evolucionar mi personaje', description: 'Segunda clase con Sebina', to: '/guia/evoluciones#sebina' }, wingsOption(2)] }
  }
  if (evo && num === 3) {
    return { id: 'evo-3', title: '¿Qué quieres hacer?', options: [{ label: 'Evolucionar mi personaje', description: 'Tercera evolución: Barracks y Refuge', to: '/guia/evoluciones#tercera' }, wingsOption(3)] }
  }
  if (evo && num === 1) {
    return { id: 'evo-1', title: '¿Qué quieres hacer?', options: [{ label: 'Ver evoluciones de personaje', description: 'Elige tu clase', to: '/guia/evoluciones' }, wingsOption(1)] }
  }
  return actionIntent(query, lookup)
}
